/**
 * W3 — the pure rules behind search.
 *
 * The api already decided what matches (`GET /api/search`: a substring of `messages.body`,
 * tombstones and `system`/`call` rows excluded, membership enforced) and in what order (newest
 * first). What is left for the client is what a *query* is, how one page splits into the two groups
 * the design shows, and what the line above them says — pure, pinned by `search.test.ts`, and never
 * re-derived at a call site (the rule `lib/chat.ts` follows for the bubble's actions).
 */
import type { SearchHit } from '@olmt/shared'
import { isolateAuto } from './bidi'
import type { MessageKey } from './i18n'

/** The api's ceiling for one search page (`MAX_LIMIT` in `apps/api/src/routes/search.ts`). */
export const SEARCH_MAX_LIMIT = 100

/** One page: the api's own default, and the page the design's "More" continues from. */
export const SEARCH_PAGE_SIZE = 30

/** The api refuses a longer query — "a query longer than this is not a search, it is a paste". */
export const SEARCH_MAX_QUERY_LENGTH = 100

/** The two groups the results are shown in. */
export type SearchGroups = { messages: SearchHit[]; media: SearchHit[] }

/** The kinds that are a *thing* rather than a sentence: the row's second line says which. */
const MEDIA_KIND: Record<string, true> = { image: true, video: true, file: true, voice: true }

/**
 * A trimmed query of 1–100 characters is a query; anything else is not worth a request. The field
 * asks this before it debounces and the store never sees a query the rule rejected, so "empty",
 * "spaces only" and "a 200-character paste" have one answer in one place.
 *
 * The query is matched literally by the api — `%`, `_` and `\` are characters there — so nothing
 * here escapes anything (docs/CONTRACT.md §4 *Search*).
 */
export function isSearchableQuery(value: string): boolean {
  const query = value.trim()
  return query.length >= 1 && query.length <= SEARCH_MAX_QUERY_LENGTH
}

/**
 * Splits one page of hits into the design's two groups, keeping the api's newest-first order inside
 * each. `media` is the kinds that carry a picture, a clip, a file or a voice note; everything else
 * is a message. A hit is always something a member wrote — the api never returns a tombstone, a
 * `system` row or a `call` row — so no group has to hide anything.
 */
export function groupHits(hits: SearchHit[]): SearchGroups {
  const messages: SearchHit[] = []
  const media: SearchHit[] = []
  for (const hit of hits) {
    if (MEDIA_KIND[hit.message.kind]) media.push(hit)
    else messages.push(hit)
  }
  return { messages, media }
}

/** The line above the groups: a dictionary key plus its placeholders, never a sentence. */
export type SearchSummary = { key: MessageKey; vars: { count: number; query: string } }

/**
 * What the line above the groups says, or `null` when there is nothing to summarise — an empty page
 * shows the empty state instead, so this is the only place the two cases are told apart.
 *
 * The count decides the key: the chrome has no plural machinery, so "1 result" and "3 results" are
 * two entries (docs/DESIGN.md §14 — anything a test pins is a key, not a sentence). `hasMore` is
 * the api's answer, not ours: a page is a window, and "3 or more results" is the honest way to say
 * so while more may exist.
 *
 * The query comes back FSI/PDI-isolated: it is the reader's own text glued into a chrome sentence,
 * and an Arabic query inside the English sentence (or the reverse) must not reorder the words
 * around it (`isolateAuto`, docs/DESIGN.md §7.9).
 */
export function searchSummary(query: string, hits: SearchHit[], hasMore: boolean): SearchSummary | null {
  const count = hits.length
  if (count === 0) return null
  const key: MessageKey = hasMore
    ? 'search.resultsPartial'
    : count === 1
      ? 'search.resultsOne'
      : 'search.resultsOther'
  return { key, vars: { count, query: isolateAuto(query) } }
}

/**
 * Folds a wider page into the hits already held. The api has no search cursor — the only way
 * further into a result set is a bigger `limit` — so "More" re-asks with one and merges the answer
 * here: one row per message id, newest first, which is the order the api itself returns (`ORDER BY
 * m.id DESC`, and ids are UUIDv7, so string order is time order). Merging rather than replacing
 * keeps a page that arrived while a newer message shifted the window.
 */
export function mergeHits(previous: SearchHit[], next: SearchHit[]): SearchHit[] {
  const byId = new Map<string, SearchHit>()
  for (const hit of previous) byId.set(hit.message.id, hit)
  for (const hit of next) byId.set(hit.message.id, hit)
  return [...byId.values()].sort((a, b) => (a.message.id < b.message.id ? 1 : -1))
}
